import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../api/client";
import type { AutonomousPolicy, AutonomousOrderLog } from "../../api/types";
import { S } from "../../styles";

/** Turn a failed policy save into something an admin can act on.
 *
 * A rejected part class is the one failure the admin can fix themselves, and
 * it is invisible unless the field-level message survives to the banner.
 */
function describeSaveFailure(err: unknown): string {
  if (err instanceof ApiError && err.status === 422) {
    try {
      const parsed = JSON.parse(err.detail) as {
        detail?: { msg?: string }[];
      };
      const msg = parsed.detail?.[0]?.msg;
      if (msg) return `Policy not saved — ${msg.replace(/^Value error, /, "")}`;
    } catch {
      // Fall through to the generic message below.
    }
    return "Policy not saved — eligible part classes must be one of rotable, repairable, expendable, consumable, standard.";
  }
  return "Failed to save policy.";
}

export function AutonomousOrderingPanel() {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [policyDraft, setPolicyDraft] = useState<Partial<AutonomousPolicy>>({});

  const { data: policy, isLoading: policyLoading } = useQuery({
    queryKey: ["autonomous-policy"],
    queryFn: api.getAutonomousPolicy,
  });

  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ["autonomous-log"],
    queryFn: () => api.listAutonomousLog(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["autonomous-policy"] });
    void queryClient.invalidateQueries({ queryKey: ["autonomous-log"] });
  };

  const saveMutation = useMutation({
    mutationFn: (data: Partial<AutonomousPolicy>) =>
      api.upsertAutonomousPolicy(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(["autonomous-policy"], updated);
      setMsg("✅ Policy saved.");
      setEditMode(false);
    },
    onError: (err) => setMsg(`❌ ${describeSaveFailure(err)}`),
  });

  const killSwitchMutation = useMutation({
    mutationFn: api.killSwitchAutonomousOrdering,
    onSuccess: () => {
      setMsg("🛑 Autonomous ordering disabled immediately.");
      invalidate();
    },
    onError: () => setMsg("❌ Kill-switch failed."),
  });

  const enableMutation = useMutation({
    mutationFn: api.enableAutonomousOrdering,
    onSuccess: () => {
      setMsg("✅ Autonomous ordering enabled.");
      invalidate();
    },
    onError: () => setMsg("❌ Failed to enable."),
  });

  const startEdit = () => {
    if (policy) {
      setPolicyDraft({
        eligible_categories: policy.eligible_categories,
        spend_ceiling_per_transaction_usd:
          policy.spend_ceiling_per_transaction_usd,
        spend_ceiling_per_period_usd: policy.spend_ceiling_per_period_usd,
        period_days: policy.period_days,
        confidence_threshold: policy.confidence_threshold,
        itar_block_enabled: policy.itar_block_enabled,
        notes: policy.notes ?? "",
      });
    }
    setEditMode(true);
  };

  const logs = logData?.items ?? [];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h2 style={S.h2}>🤖 Autonomous Ordering</h2>
        {policy && (
          <span
            style={{
              padding: "2px 10px",
              borderRadius: 12,
              fontSize: 12,
              background: policy.enabled ? "#d1fae5" : "#fee2e2",
              color: policy.enabled ? "#065f46" : "#991b1b",
              fontWeight: 600,
            }}
          >
            {policy.enabled ? "ENABLED" : "DISABLED"}
          </span>
        )}
      </div>

      {msg && (
        <div
          style={{
            marginBottom: 12,
            color: msg.startsWith("❌") ? "#dc2626" : "#059669",
          }}
        >
          {msg}
        </div>
      )}

      {/* Kill-switch controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button
          style={{ ...S.btn, background: "#dc2626", color: "#fff" }}
          onClick={() => {
            if (confirm("Disable autonomous ordering immediately?")) {
              killSwitchMutation.mutate();
            }
          }}
        >
          🛑 Kill Switch — Disable Now
        </button>
        <button
          style={{ ...S.btn, background: "#059669", color: "#fff" }}
          onClick={() => enableMutation.mutate()}
        >
          ▶ Enable Autonomous Ordering
        </button>
        <button
          style={{ ...S.btn, background: "#4f46e5", color: "#fff" }}
          onClick={startEdit}
        >
          ✏ Edit Policy
        </button>
      </div>

      {/* Policy details */}
      {policyLoading ? (
        <p style={S.muted}>Loading policy…</p>
      ) : policy ? (
        editMode ? (
          <PolicyEditor
            draft={policyDraft}
            onChange={setPolicyDraft}
            onSave={() => saveMutation.mutate(policyDraft)}
            onCancel={() => setEditMode(false)}
            saving={saveMutation.isPending}
          />
        ) : (
          <PolicyCard policy={policy} />
        )
      ) : null}

      {/* Autonomous Order Log */}
      <h3 style={{ ...S.h2, marginTop: 24 }}>Evaluation Log</h3>
      {logLoading ? (
        <p style={S.muted}>Loading log…</p>
      ) : logs.length === 0 ? (
        <p style={S.muted}>No autonomous ordering evaluations yet.</p>
      ) : (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr style={{ background: "#f3f4f6" }}>
              {["Time", "Executed", "Anomaly", "PO Number", "Reason"].map(
                (h) => (
                  <th
                    key={h}
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <LogRow key={log.id} log={log} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PolicyCard({ policy }: { policy: AutonomousPolicy }) {
  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: 12,
      }}
    >
      <Stat
        label="Confidence Threshold"
        value={`≥ ${(policy.confidence_threshold * 100).toFixed(0)}%`}
      />
      <Stat
        label="Per-Transaction Ceiling"
        value={`$${policy.spend_ceiling_per_transaction_usd.toLocaleString()}`}
      />
      <Stat
        label="Period Ceiling"
        value={`$${policy.spend_ceiling_per_period_usd.toLocaleString()} / ${policy.period_days}d`}
      />
      <Stat
        label="ITAR Block"
        value={policy.itar_block_enabled ? "Enabled" : "Disabled"}
        warn={!policy.itar_block_enabled}
      />
      <Stat
        label="Eligible Part Classes"
        value={
          policy.eligible_categories.length === 0
            ? "All classes"
            : policy.eligible_categories.join(", ")
        }
      />
      <Stat
        label="Approved Suppliers"
        value={
          policy.approved_supplier_ids.length === 0
            ? "Any supplier"
            : `${policy.approved_supplier_ids.length} supplier(s)`
        }
      />
      {policy.notes && <Stat label="Notes" value={policy.notes} />}
    </div>
  );
}

function Stat({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontWeight: 600, color: warn ? "#dc2626" : "#111827" }}>
        {value}
      </div>
    </div>
  );
}

function PolicyEditor({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  draft: Partial<AutonomousPolicy>;
  onChange: (d: Partial<AutonomousPolicy>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  // Kept as raw text so a half-typed list survives — parsing on every keystroke
  // and rendering the parsed array back would eat the comma as you type it,
  // making a second class impossible to enter.
  const [classesText, setClassesText] = useState(() =>
    (draft.eligible_categories ?? []).join(", "),
  );

  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <h4 style={{ margin: "0 0 12px" }}>Edit Policy</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={S.label}>
          Confidence Threshold (0–1)
          <input
            style={S.input}
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={draft.confidence_threshold ?? ""}
            onChange={(e) =>
              onChange({
                ...draft,
                confidence_threshold: parseFloat(e.target.value),
              })
            }
          />
        </label>
        <label style={S.label}>
          Per-Transaction Ceiling (USD)
          <input
            style={S.input}
            type="number"
            min="0"
            value={draft.spend_ceiling_per_transaction_usd ?? ""}
            onChange={(e) =>
              onChange({
                ...draft,
                spend_ceiling_per_transaction_usd: parseFloat(e.target.value),
              })
            }
          />
        </label>
        <label style={S.label}>
          Period Ceiling (USD)
          <input
            style={S.input}
            type="number"
            min="0"
            value={draft.spend_ceiling_per_period_usd ?? ""}
            onChange={(e) =>
              onChange({
                ...draft,
                spend_ceiling_per_period_usd: parseFloat(e.target.value),
              })
            }
          />
        </label>
        <label style={S.label}>
          Period (days)
          <input
            style={S.input}
            type="number"
            min="1"
            value={draft.period_days ?? ""}
            onChange={(e) =>
              onChange({ ...draft, period_days: parseInt(e.target.value) })
            }
          />
        </label>
        <label style={S.label}>
          Eligible Part Classes (comma-separated, empty = all)
          <input
            style={S.input}
            type="text"
            placeholder="e.g. consumable, standard, expendable"
            value={classesText}
            onChange={(e) => {
              setClassesText(e.target.value);
              onChange({
                ...draft,
                eligible_categories: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              });
            }}
          />
          <span style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
            One or more of rotable, repairable, expendable, consumable,
            standard. Matched against the part's class in the part master; parts
            missing from the master, or with no class recorded, are always
            routed to a human. The ITAR block is separate and still reads the
            part's ATA chapter.
          </span>
        </label>
        <label style={S.label}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={draft.itar_block_enabled ?? true}
              onChange={(e) =>
                onChange({ ...draft, itar_block_enabled: e.target.checked })
              }
            />
            Block ITAR-controlled parts
          </span>
        </label>
        <label style={{ ...S.label, gridColumn: "1 / -1" }}>
          Notes
          <input
            style={S.input}
            type="text"
            value={draft.notes ?? ""}
            onChange={(e) => onChange({ ...draft, notes: e.target.value })}
          />
        </label>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button style={S.btn} onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          style={{ ...S.btn, background: "#6b7280", color: "#fff" }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function LogRow({ log }: { log: AutonomousOrderLog }) {
  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td style={{ padding: "6px 10px", color: "#6b7280" }}>
        {new Date(log.created_at).toLocaleString()}
      </td>
      <td style={{ padding: "6px 10px" }}>
        <span
          style={{
            background: log.executed_autonomously ? "#d1fae5" : "#fef3c7",
            color: log.executed_autonomously ? "#065f46" : "#92400e",
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {log.executed_autonomously ? "Auto" : "Human"}
        </span>
      </td>
      <td style={{ padding: "6px 10px" }}>
        {log.anomaly_flagged && (
          <span style={{ color: "#dc2626", fontWeight: 700 }}>⚠ Flagged</span>
        )}
      </td>
      <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>
        {log.purchase_order_id ? log.purchase_order_id.slice(0, 8) + "…" : "—"}
      </td>
      <td
        style={{
          padding: "6px 10px",
          color: "#374151",
          maxWidth: 320,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {log.route_reason}
      </td>
    </tr>
  );
}
